import React from 'react';
import { Box, Typography } from '@mui/material';
import { Warning as WarningIcon, Error as ErrorIcon, PriorityHigh as PriorityHighIcon } from '@mui/icons-material';

interface SelectionWarningOverlayProps {
  open: boolean;
  count: number;
}

const SelectionWarningOverlay: React.FC<SelectionWarningOverlayProps> = ({ open, count }) => {
  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(255, 0, 0, 0.9)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background:
            'repeating-radial-gradient(circle at 50% 50%, rgba(0,0,0,0), rgba(0,0,0,0) 40px, rgba(0,0,0,0.2) 40px, rgba(0,0,0,0.2) 80px)',
          animation: 'pulse-bg 2s infinite',
          '@keyframes pulse-bg': {
            '0%': { transform: 'scale(1)', opacity: 0.7 },
            '50%': { transform: 'scale(1.2)', opacity: 1 },
            '100%': { transform: 'scale(1)', opacity: 0.7 },
          },
          zIndex: 1,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          right: '5%',
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          backgroundColor: 'yellow',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          animation: 'spin 2s linear infinite',
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
          zIndex: 5,
          border: '10px solid black',
          boxShadow: '0 0 30px rgba(255, 255, 0, 0.8)',
        }}
      >
        <WarningIcon sx={{ fontSize: 80, color: 'black' }} />
      </Box>

      <Box
        sx={{
          backgroundColor: 'black',
          color: 'white',
          border: '15px solid yellow',
          borderRadius: '50%',
          width: '250px',
          height: '250px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '20px auto',
          boxShadow: '0 0 50px rgba(255, 255, 0, 0.8)',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: 900,
            fontSize: '6rem',
            textShadow: '0 0 20px rgba(255, 255, 255, 0.7)',
            animation: 'pulse 0.5s infinite alternate',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '100%': { transform: 'scale(1.1)' },
            },
          }}
        >
          {count}
        </Typography>
      </Box>

      <Typography
        variant="h1"
        sx={{
          color: 'white',
          textAlign: 'center',
          mb: 3,
          fontWeight: 900,
          textShadow: '5px 5px 10px rgba(0,0,0,0.9)',
          animation: 'flash-warning 0.5s infinite',
          '@keyframes flash-warning': {
            '0%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.8, transform: 'scale(1.05)' },
            '100%': { opacity: 1, transform: 'scale(1)' },
          },
          letterSpacing: '5px',
          zIndex: 2,
          fontSize: { xs: '3rem', sm: '5rem', md: '8rem' },
        }}
      >
        WARNING!
      </Typography>

      <Box
        sx={{
          maxWidth: '900px',
          textAlign: 'center',
          backgroundColor: 'black',
          border: '8px dashed yellow',
          borderRadius: '20px',
          padding: 4,
          marginBottom: 4,
          zIndex: 2,
          boxShadow: '0 0 30px rgba(0, 0, 0, 0.9)',
        }}
      >
        <Typography
          variant="h3"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            textShadow: '3px 3px 6px rgba(0,0,0,0.8)',
            mb: 3,
          }}
        >
          YOU ARE ABOUT TO REPORT ISSUES FOR
        </Typography>
        <Typography
          variant="h1"
          sx={{
            color: 'yellow',
            fontWeight: 'bold',
            mb: 3,
            animation: 'pulse-text 1s infinite',
            '@keyframes pulse-text': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' },
              '100%': { transform: 'scale(1)' },
            },
            textShadow: '0 0 20px rgba(255, 255, 0, 0.7)',
          }}
        >
          {count} DEVICES!
        </Typography>
        <Typography variant="h4" sx={{ color: 'red', textShadow: '0 0 10px rgba(255, 0, 0, 0.7)' }}>
          THIS IS NOT A DRILL!
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxWidth: '800px',
          zIndex: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: 'white',
            textAlign: 'center',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            fontStyle: 'italic',
            backgroundColor: 'rgba(0,0,0,0.7)',
            padding: 2,
            borderRadius: 2,
          }}
        >
          "With great device selection comes great responsibility."
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: 'white',
            textAlign: 'center',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            backgroundColor: 'rgba(0,0,0,0.7)',
            padding: 2,
            borderRadius: 2,
          }}
        >
          Are you really, absolutely, definitely sure?
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: '5%',
          left: '5%',
          animation: 'float 3s infinite ease-in-out',
          '@keyframes float': {
            '0%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
            '100%': { transform: 'translateY(0px) rotate(360deg)' },
          },
        }}
      >
        <ErrorIcon sx={{ color: 'yellow', fontSize: 80 }} />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          animation: 'float2 4s infinite ease-in-out',
          '@keyframes float2': {
            '0%': { transform: 'translateX(0px) rotate(0deg)' },
            '50%': { transform: 'translateX(30px) rotate(-180deg)' },
            '100%': { transform: 'translateX(0px) rotate(-360deg)' },
          },
        }}
      >
        <PriorityHighIcon sx={{ color: 'yellow', fontSize: 70 }} />
      </Box>
    </Box>
  );
};

export default SelectionWarningOverlay;


